import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarBlank, ChartBar, Users, Ticket, ChartLine, ChartPieSlice, ArrowsCounterClockwise, UserMinus, Buildings } from "@phosphor-icons/react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell,
} from "recharts";
import { toast } from "sonner";
import { format, subDays } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ["#002FA7", "#F59E0B", "#8B5CF6", "#10B981", "#EF4444", "#06B6D4", "#EC4899"];

const StatTile = ({ label, value, sub, accent = "blue", testid }) => {
  const palette = {
    blue: "border-l-[#002FA7] bg-blue-50/30",
    amber: "border-l-[#F59E0B] bg-amber-50/30",
    violet: "border-l-[#8B5CF6] bg-violet-50/30",
    emerald: "border-l-[#10B981] bg-emerald-50/30",
    rose: "border-l-[#EF4444] bg-rose-50/30",
  }[accent];
  return (
    <Card className={`border border-[#E2E8F0] border-l-4 ${palette}`} data-testid={testid}>
      <CardContent className="p-4">
        <div className="text-xs uppercase tracking-[0.2em] text-[#64748B]">{label}</div>
        <div className="font-heading text-3xl font-bold text-[#0A0A0A] mt-1">{value}</div>
        {sub && <div className="text-xs text-[#64748B] mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
};

export default function AnalyticsPage() {
  const [startDate, setStartDate] = useState(subDays(new Date(), 30));
  const [endDate, setEndDate] = useState(new Date());
  const [brandFilter, setBrandFilter] = useState("");
  const [brands, setBrands] = useState([]);

  const [frequency, setFrequency] = useState(null);
  const [brandPerf, setBrandPerf] = useState([]);
  const [voucherFunnel, setVoucherFunnel] = useState(null);
  const [cohort, setCohort] = useState([]);
  const [sourceSplit, setSourceSplit] = useState(null);
  const [inactiveDays, setInactiveDays] = useState(60);
  const [inactive, setInactive] = useState({ customers: [], count: 0 });
  const [phoneQuery, setPhoneQuery] = useState("");
  const [journey, setJourney] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [cohortGroup, setCohortGroup] = useState("day");

  useEffect(() => { fetchBrands(); }, []);
  useEffect(() => { fetchAll(); }, [startDate, endDate, brandFilter, cohortGroup]);

  const fetchBrands = async () => {
    try { const res = await axios.get(`${API}/brands`); setBrands(res.data.brands || []); } catch {}
  };

  const fetchAll = async () => {
    try {
      setIsLoading(true);
      const sd = format(startDate, "yyyy-MM-dd");
      const ed = format(endDate, "yyyy-MM-dd");
      const brandQ = brandFilter ? `&brand=${encodeURIComponent(brandFilter)}` : "";
      const [freqRes, perfRes, funnelRes, cohortRes, sourceRes] = await Promise.all([
        axios.get(`${API}/analytics/customer-frequency?start_date=${sd}&end_date=${ed}${brandQ}`),
        axios.get(`${API}/analytics/brand-performance?start_date=${sd}&end_date=${ed}`),
        axios.get(`${API}/analytics/voucher-funnel${brandFilter ? `?brand=${encodeURIComponent(brandFilter)}` : ""}`),
        axios.get(`${API}/analytics/cohort-trend?start_date=${sd}&end_date=${ed}&group_by=${cohortGroup}`),
        axios.get(`${API}/analytics/customer-source-split?start_date=${sd}&end_date=${ed}`),
      ]);
      setFrequency(freqRes.data);
      setBrandPerf(perfRes.data.brands || []);
      setVoucherFunnel(funnelRes.data);
      setCohort(cohortRes.data.data || []);
      setSourceSplit(sourceRes.data);
    } catch (e) { toast.error("Failed to load analytics"); }
    finally { setIsLoading(false); }
  };

  const fetchInactive = async () => {
    try {
      const brandQ = brandFilter ? `&brand=${encodeURIComponent(brandFilter)}` : "";
      const res = await axios.get(`${API}/analytics/inactive-customers?days=${inactiveDays}${brandQ}&limit=200`);
      setInactive(res.data);
      toast.success(`Found ${res.data.count} inactive customers`);
    } catch { toast.error("Failed to load inactive customers"); }
  };

  const fetchJourney = async () => {
    if (!phoneQuery) { toast.error("Enter a phone number"); return; }
    try {
      const res = await axios.get(`${API}/analytics/customers/${encodeURIComponent(phoneQuery)}/journey`);
      setJourney(res.data);
    } catch (e) {
      if (e?.response?.status === 404) toast.error("Customer not found");
      else toast.error("Failed to load journey");
      setJourney(null);
    }
  };

  const freqBuckets = frequency
    ? Object.entries(frequency.frequency_buckets || {}).map(([k, v]) => ({ bucket: `${k} visit${k === "1" ? "" : "s"}`, customers: v }))
    : [];
  const sourcePieData = sourceSplit
    ? Object.entries(sourceSplit.by_source || {}).map(([k, v], i) => ({ name: k.toUpperCase(), value: v, color: COLORS[i % COLORS.length] }))
    : [];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Customer Analytics</h2>
          <p className="text-[#64748B] text-sm mt-1">One-timer, repeat customers, brand performance & re-engagement insights</p>
        </div>
      </div>

      {/* Filter Bar */}
      <Card className="border border-[#E2E8F0]">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-44 justify-start font-normal" data-testid="analytics-start-date">
                    <CalendarBlank size={16} className="mr-2" />{format(startDate, "MMM dd, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={startDate} onSelect={(d) => d && setStartDate(d)} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-44 justify-start font-normal" data-testid="analytics-end-date">
                    <CalendarBlank size={16} className="mr-2" />{format(endDate, "MMM dd, yyyy")}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={endDate} onSelect={(d) => d && setEndDate(d)} initialFocus /></PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Quick</Label>
              <Select onValueChange={(v) => { const days = parseInt(v); setEndDate(new Date()); setStartDate(subDays(new Date(), days)); }}>
                <SelectTrigger className="w-32" data-testid="quick-range"><SelectValue placeholder="Range" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                  <SelectItem value="365">Last 365 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand</Label>
              <Select value={brandFilter || "_all"} onValueChange={(v) => setBrandFilter(v === "_all" ? "" : v)}>
                <SelectTrigger className="w-52" data-testid="brand-filter"><SelectValue placeholder="All Brands" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All Brands</SelectItem>
                  {brands.map((b) => (<SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            {isLoading && <div className="text-sm text-[#64748B]">Loading…</div>}
          </div>
        </CardContent>
      </Card>

      {/* Headline KPIs */}
      {frequency && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          <StatTile testid="kpi-unique" label="Unique Customers" value={frequency.totals.unique_customers.toLocaleString()} accent="blue" />
          <StatTile testid="kpi-one-timer" label="One-Timer" value={frequency.one_timer_count.toLocaleString()} sub={`${frequency.one_timer_percent}% of base`} accent="amber" />
          <StatTile testid="kpi-repeat" label="Repeat Customers" value={frequency.repeat_count.toLocaleString()} sub={`${frequency.repeat_percent}% of base`} accent="emerald" />
          <StatTile testid="kpi-cross-brand" label="Cross-Brand Loyalty" value={frequency.cross_brand_loyalty_count.toLocaleString()} sub="Visited 2+ brands" accent="violet" />
          <StatTile testid="kpi-revenue" label="Revenue" value={`₹${frequency.totals.total_spend.toLocaleString()}`} sub={`Avg ₹${frequency.totals.avg_spend_per_customer} / customer`} accent="rose" />
        </div>
      )}

      <Tabs defaultValue="frequency" className="w-full">
        <TabsList className="bg-white border border-[#E2E8F0]">
          <TabsTrigger value="frequency" data-testid="tab-frequency"><ArrowsCounterClockwise size={16} className="mr-2" />Frequency</TabsTrigger>
          <TabsTrigger value="brands" data-testid="tab-brands"><Buildings size={16} className="mr-2" />Brands</TabsTrigger>
          <TabsTrigger value="vouchers" data-testid="tab-vouchers"><Ticket size={16} className="mr-2" />Voucher Funnel</TabsTrigger>
          <TabsTrigger value="cohort" data-testid="tab-cohort"><ChartLine size={16} className="mr-2" />Acquisition</TabsTrigger>
          <TabsTrigger value="inactive" data-testid="tab-inactive"><UserMinus size={16} className="mr-2" />Inactive</TabsTrigger>
          <TabsTrigger value="journey" data-testid="tab-journey"><Users size={16} className="mr-2" />Journey</TabsTrigger>
        </TabsList>

        {/* FREQUENCY */}
        <TabsContent value="frequency" className="mt-4 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="border border-[#E2E8F0]">
              <CardHeader><CardTitle className="text-[#0A0A0A] flex items-center gap-2 text-base"><ChartBar size={18} />Visit Frequency Distribution</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={freqBuckets}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="bucket" stroke="#64748B" />
                    <YAxis stroke="#64748B" />
                    <Tooltip />
                    <Bar dataKey="customers" fill="#002FA7" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
            <Card className="border border-[#E2E8F0]">
              <CardHeader><CardTitle className="text-[#0A0A0A] flex items-center gap-2 text-base"><Users size={18} />Top 20 Spenders</CardTitle></CardHeader>
              <CardContent className="max-h-[300px] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Phone</TableHead><TableHead>Visits</TableHead><TableHead>Spend</TableHead><TableHead>Brands</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(frequency?.top_spenders || []).map((t) => (
                      <TableRow key={t.phone} data-testid={`top-spender-${t.phone}`}>
                        <TableCell className="font-mono text-xs">{t.phone}</TableCell>
                        <TableCell>{t.visits}</TableCell>
                        <TableCell>₹{t.total_spend.toLocaleString()}</TableCell>
                        <TableCell className="text-xs">{t.brands.slice(0, 3).join(", ")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* BRANDS */}
        <TabsContent value="brands" className="mt-4 space-y-4">
          <Card className="border border-[#E2E8F0]">
            <CardHeader><CardTitle className="text-[#0A0A0A] text-base">Brand Performance</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={brandPerf}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="brand" stroke="#64748B" angle={-20} textAnchor="end" interval={0} height={60} />
                  <YAxis stroke="#64748B" />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="unique_customers" fill="#002FA7" name="Unique Customers" />
                  <Bar dataKey="repeat_customers" fill="#10B981" name="Repeat" />
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 overflow-x-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Brand</TableHead><TableHead>Unique Customers</TableHead><TableHead>Visits</TableHead><TableHead>Revenue</TableHead><TableHead>Avg Bill</TableHead><TableHead>Repeat Rate</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {brandPerf.map((b) => (
                      <TableRow key={b.brand} data-testid={`brand-row-${b.brand}`}>
                        <TableCell className="font-medium">{b.brand}</TableCell>
                        <TableCell>{b.unique_customers.toLocaleString()}</TableCell>
                        <TableCell>{b.total_visits.toLocaleString()}</TableCell>
                        <TableCell>₹{b.total_revenue.toLocaleString()}</TableCell>
                        <TableCell>₹{b.avg_bill.toLocaleString()}</TableCell>
                        <TableCell><Badge variant="secondary">{b.repeat_rate_percent}%</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* VOUCHER FUNNEL */}
        <TabsContent value="vouchers" className="mt-4 space-y-4">
          {voucherFunnel && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatTile testid="vf-total" label="Total Vouchers" value={voucherFunnel.total.toLocaleString()} accent="blue" />
              <StatTile testid="vf-available" label="Available" value={voucherFunnel.available.toLocaleString()} accent="emerald" />
              <StatTile testid="vf-assigned" label="Assigned" value={voucherFunnel.assigned.toLocaleString()} sub={`${voucherFunnel.assignment_rate_percent}% assignment rate`} accent="amber" />
              <StatTile testid="vf-redeemed" label="Redeemed" value={voucherFunnel.redeemed.toLocaleString()} sub={`${voucherFunnel.redemption_rate_percent}% of issued`} accent="violet" />
            </div>
          )}
          {sourceSplit && (
            <Card className="border border-[#E2E8F0]">
              <CardHeader><CardTitle className="text-[#0A0A0A] text-base flex items-center gap-2"><ChartPieSlice size={18} />Customer Acquisition Source</CardTitle></CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={sourcePieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                      {sourcePieData.map((entry, i) => (<Cell key={i} fill={entry.color} />))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* COHORT */}
        <TabsContent value="cohort" className="mt-4 space-y-4">
          <Card className="border border-[#E2E8F0]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-[#0A0A0A] text-base">New Customer Acquisition Trend</CardTitle>
              <Select value={cohortGroup} onValueChange={setCohortGroup}>
                <SelectTrigger className="w-32" data-testid="cohort-group"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="day">Daily</SelectItem>
                  <SelectItem value="week">Weekly</SelectItem>
                  <SelectItem value="month">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={cohort}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="period" stroke="#64748B" />
                  <YAxis stroke="#64748B" />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="qr_code" stroke="#002FA7" name="QR Code" strokeWidth={2} />
                  <Line type="monotone" dataKey="adsr" stroke="#F59E0B" name="ADSR" strokeWidth={2} />
                  <Line type="monotone" dataKey="manual" stroke="#8B5CF6" name="Manual" strokeWidth={2} />
                  <Line type="monotone" dataKey="total" stroke="#10B981" name="Total" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>

        {/* INACTIVE */}
        <TabsContent value="inactive" className="mt-4 space-y-4">
          <Card className="border border-[#E2E8F0]">
            <CardHeader><CardTitle className="text-[#0A0A0A] text-base">Inactive Customers (Re-Engagement Pool)</CardTitle></CardHeader>
            <CardContent>
              <div className="flex flex-wrap items-end gap-4 mb-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Inactive for at least (days)</Label>
                  <Input type="number" value={inactiveDays} onChange={(e) => setInactiveDays(parseInt(e.target.value) || 60)} className="w-32" data-testid="inactive-days-input" />
                </div>
                <Button onClick={fetchInactive} data-testid="run-inactive" className="bg-[#002FA7] hover:bg-[#002080]">Run Report</Button>
                <div className="text-sm text-[#64748B]">Found <strong>{inactive.count}</strong> inactive customers</div>
              </div>
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader><TableRow><TableHead>Phone</TableHead><TableHead>Name</TableHead><TableHead>Last Visit</TableHead><TableHead>Total Visits</TableHead><TableHead>Spend</TableHead><TableHead>Last Brand</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {(inactive.customers || []).map((c) => (
                      <TableRow key={c.phone} data-testid={`inactive-row-${c.phone}`}>
                        <TableCell className="font-mono text-xs">{c.phone}</TableCell>
                        <TableCell>{c.name || "-"}</TableCell>
                        <TableCell className="text-xs">{c.last_visit ? c.last_visit.slice(0, 10) : "-"}</TableCell>
                        <TableCell>{c.visits}</TableCell>
                        <TableCell>₹{c.total_spend.toLocaleString()}</TableCell>
                        <TableCell>{c.last_brand}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CUSTOMER JOURNEY */}
        <TabsContent value="journey" className="mt-4 space-y-4">
          <Card className="border border-[#E2E8F0]">
            <CardHeader><CardTitle className="text-[#0A0A0A] text-base">Individual Customer 360°</CardTitle></CardHeader>
            <CardContent>
              <div className="flex items-end gap-3 mb-6">
                <div className="space-y-2 flex-1 max-w-md">
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Phone Number (10 or 12 digits)</Label>
                  <Input value={phoneQuery} onChange={(e) => setPhoneQuery(e.target.value)} placeholder="9198XXXXXXXX" data-testid="journey-phone-input" />
                </div>
                <Button onClick={fetchJourney} data-testid="run-journey" className="bg-[#002FA7] hover:bg-[#002080]">View Journey</Button>
              </div>
              {journey && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <StatTile testid="j-visits" label="Visits" value={journey.summary.total_visits} accent="blue" />
                    <StatTile testid="j-spend" label="Total Spend" value={`₹${journey.summary.total_spend.toLocaleString()}`} accent="emerald" />
                    <StatTile testid="j-avg" label="Avg Bill" value={`₹${journey.summary.avg_bill.toLocaleString()}`} accent="amber" />
                    <StatTile testid="j-type" label="Customer Type" value={journey.summary.is_repeat ? "Repeat" : "One-Timer"} accent={journey.summary.is_repeat ? "emerald" : "amber"} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card className="border border-[#E2E8F0]"><CardHeader><CardTitle className="text-sm">Profile</CardTitle></CardHeader><CardContent className="text-sm space-y-1">
                      <div><span className="text-[#64748B]">Phone:</span> {journey.customer.phone}</div>
                      <div><span className="text-[#64748B]">Name:</span> {journey.customer.name || "-"}</div>
                      <div><span className="text-[#64748B]">Source:</span> {journey.customer.registration_source}</div>
                      <div><span className="text-[#64748B]">Brand:</span> {journey.customer.brand || "-"}</div>
                      <div><span className="text-[#64748B]">Voucher:</span> {journey.customer.voucher_code || "-"}</div>
                      <div><span className="text-[#64748B]">Brands visited:</span> {journey.summary.brands_visited.join(", ") || "-"}</div>
                    </CardContent></Card>
                    <Card className="border border-[#E2E8F0]"><CardHeader><CardTitle className="text-sm">Last 5 WhatsApp Messages</CardTitle></CardHeader><CardContent className="text-xs max-h-64 overflow-y-auto space-y-2">
                      {(journey.whatsapp_messages || []).slice(0, 8).map((m) => (
                        <div key={m.id} className="border-l-2 pl-2" style={{ borderColor: m.direction === "inbound" ? "#10B981" : "#002FA7" }}>
                          <div className="text-[#64748B]">{m.timestamp?.slice(0, 19)} · <Badge variant="secondary">{m.direction}</Badge></div>
                          <div>{m.content?.slice(0, 100)}</div>
                        </div>
                      ))}
                    </CardContent></Card>
                  </div>
                  <Card className="border border-[#E2E8F0]"><CardHeader><CardTitle className="text-sm">Transactions ({journey.transactions.length})</CardTitle></CardHeader><CardContent className="max-h-[300px] overflow-y-auto">
                    <Table><TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Brand</TableHead><TableHead>Bill No</TableHead><TableHead>Amount</TableHead></TableRow></TableHeader><TableBody>
                      {journey.transactions.map((t) => (
                        <TableRow key={t.id}><TableCell className="text-xs">{(t.bill_date || "").slice(0, 10)}</TableCell><TableCell>{t.brand_name}</TableCell><TableCell className="text-xs">{t.bill_no}</TableCell><TableCell>₹{(t.bill_amount || 0).toLocaleString()}</TableCell></TableRow>
                      ))}
                    </TableBody></Table>
                  </CardContent></Card>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
